import type { ReactNode } from 'react';
import { DatePicker, type CalendarLabels } from '@forecastify/ui';
import { useI18n, useT } from '../i18n';
import { formatDate, todayWire } from './format';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A date in a toolbar: borderless until hovered, *2024. márc 12.* when set. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`ui`'s `DatePicker`, which already steps day → month → year** — settled control #4 — with
 * the mockup's skin and the mockup's date format laid over it. The picker is the product's; only
 * how the closed trigger looks and reads is the console's.
 */
export function DateFilter({
  value,
  onChange,
  label,
}: {
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  /** The accessible name — *Created from*. The visible words sit beside it in the toolbar. */
  readonly label: string;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();

  const labels: CalendarLabels = {
    months: MONTHS[locale].slice(),
    weekdays: WEEKDAYS[locale].slice(),
    today: t('cal.today'),
    clear: t('cal.clear'),
    previousMonth: t('cal.previous'),
    nextMonth: t('cal.next'),
    placeholder: '–',
    dialogLabel: label,
  };

  return (
    <DatePicker
      value={value}
      onChange={onChange}
      today={todayWire()}
      labels={labels}
      clearable
      ariaLabel={label}
      className="w-[140px]"
      buttonClassName="border-transparent shadow-none hover:border-input hover:bg-transparent text-[13px]"
      formatValue={(wire) => formatDate(wire, locale)}
    />
  );
}

const MONTHS = {
  hu: [
    'január',
    'február',
    'március',
    'április',
    'május',
    'június',
    'július',
    'augusztus',
    'szeptember',
    'október',
    'november',
    'december',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
} as const;

/* The mockup's day-of-week heads, Monday first. */
const WEEKDAYS = {
  hu: ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
} as const;
